import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  ChevronDown,
  Home,
  Pin,
  X,
  FileText,
  User,
} from "lucide-react"
import { type OpenTab } from "@/components/features/document-tabs"
import { usePersistedState } from "@/hooks/use-persisted-state"
import { StorageKeys, StorageSchemas } from "@/lib/storage"
import {
  DocumentsExplorerInlineSection,
  type ExplorerFolderTreeNode,
} from "@/components/layout/documents-explorer-inline-section"
import { useSpacesForWorkspaces } from "@/hooks/use-spaces"
import { fetchWithAuth } from "@/lib/api"
import { dispatchOpenPeopleIndex } from "@/lib/person-view"
import {
  normalizeExplorerPath,
  shouldPreserveBrowseStateOnDocumentOpen,
} from "@/lib/explorer-tree-focus"
import { buildOpenTabIndex, isOpenInTabs, openTabPathsFromIndex } from "@/lib/open-tab-match"
import { testIds } from "@/lib/test-ids"
import { cn } from "@/lib/utils"
import { WS_ICON_ON_ACCENT, workspaceAccentFill } from "@/lib/workspace-color"
import {
  DndContext,
  closestCenter,
  DragOverlay,
} from '@dnd-kit/core'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import type { Workspace } from "@/providers/workspace-provider"
import type { SidebarShortcut } from "@/lib/api"
import {
  useWorkspaceRowContextMenu,
  WorkspaceRowContextMenuPortal,
} from "./left-sidebar.workspace-row-menu"
import { WorkspaceIcon, WorkspaceExtensionItems, SortableShortcutItem, WsNavBrowseRow, WS_NAV_CHILD_PAD } from "./left-sidebar.components"
import { KnowledgeHubBrowseSection } from "@/components/integrations/knowledge-hub/knowledge-hub-browse-section"
import { useAccountDeskSlug } from "@/hooks/use-account-desk-slug"
import { deskPathForMember, isPersonalWorkspace } from "@/lib/workspace-desk-path"
import {
  SIDEBAR_PINS_EXPAND_ID,
  expandKeyForPinnedWorkspace,
  getShortcutIcon,
  getSpacesForWorkspace,
  pinnedShortcutsForWorkspace,
  shortcutBelongsToWorkspace,
} from "./left-sidebar.utils"
import { useBrowseFolderFocus } from "@/providers/browse-folder-focus-provider"
import { collectFolderMaxUpdatedFromExplorerNodes } from "@/lib/browse-folder-focus"
import { browseExpandedStore } from "@/lib/browse-expanded-store"
import {
  readExplorerTreeCache,
  writeExplorerTreeCache,
  clearExplorerTreeCache,
  treeContainsFolderPath,
} from "@/lib/explorer-tree-cache"
import { enqueueExplorerTreeFetch } from "@/lib/explorer-tree-fetch-queue"
import { BrowseExpandedProvider } from "@/providers/browse-expanded-provider"
import { SidebarNavExpandPanel as NavExpandPanel } from "@/components/layout/sidebar/SidebarNavExpandPanel"
type SidebarTab = "browse-nav" | "browse" | "documents-tree" | "documents" | "inbox" | "graph" | "tags" | "trash" | `integration-${string}`

type FolderTreeNode = ExplorerFolderTreeNode

export interface BrowsePanelProps {
  activeTab: SidebarTab
  activeTabId?: string | null
  onSetTab: (tab: SidebarTab) => void
  onHomeClick?: (event?: React.MouseEvent) => void
  onInboxClick?: () => void
  workspaces: Workspace[]
  activeWorkspaceIds: string[]
  configuredIntegrations: ReturnType<typeof import('@/hooks/use-integrations').useIntegrations>['configuredIntegrations']
  openTabs?: OpenTab[]
  onDocumentClick?: (documentId: string, event?: React.MouseEvent) => void
  onDocumentClose?: (documentId: string) => void
  onCloseAllOpenDocuments?: () => void
  activeDocumentPath?: string | null
  /** Hub/list folder path when activeDocumentPath is unset (folder hub view). */
  selectedProjectId?: string | null
  onFolderClick?: (folderPath: string, event?: React.MouseEvent) => void
  onFolderTreeContextMenu?: (
    node: FolderTreeNode,
    event: React.MouseEvent,
    workspaceId: string,
    browseHiddenChildCount?: number,
    browseHiddenLabel?: string
  ) => void
  /** Incremented in LeftSidebar on content-revision / document create-delete so tree refetches even when this panel was unmounted (e.g. Trash / Search / etc.). */
  explorerRefreshToken?: number
  /** Called when user right-clicks a workspace row and chooses "Hide workspace" */
  onToggleWorkspace: (wsId: string) => void
  /** Show only this workspace; hide all others from browse scope. */
  onHideOtherWorkspaces?: (wsId: string) => void
  shortcuts: SidebarShortcut[]
  shortcutsLoading?: boolean
  activeShortcutId: string | null
  sensors: ReturnType<typeof import('@dnd-kit/core').useSensors>
  onShortcutDragStart: (event: DragStartEvent) => void
  onShortcutDragEnd: (event: DragEndEvent) => void
}


export function BrowsePanel({ activeTab, activeTabId, onSetTab, onHomeClick, onInboxClick, workspaces, activeWorkspaceIds, configuredIntegrations, openTabs = [], onDocumentClick, onDocumentClose, activeDocumentPath, selectedProjectId = null, onFolderClick, onFolderTreeContextMenu, explorerRefreshToken = 0, onToggleWorkspace, onHideOtherWorkspaces, shortcuts, shortcutsLoading = false, activeShortcutId, sensors, onShortcutDragStart, onShortcutDragEnd }: BrowsePanelProps) {
  const browseScrollRef = useRef<HTMLDivElement>(null)
  const browseFocus = useBrowseFolderFocus()
  const { deskSlug } = useAccountDeskSlug()
  const openTabIndex = useMemo(() => buildOpenTabIndex(openTabs), [openTabs])
  const openTabPaths = useMemo(() => openTabPathsFromIndex(openTabIndex), [openTabIndex])

  const handleTreeContextMenu = useCallback(
    (node: FolderTreeNode, event: React.MouseEvent, workspaceId: string) => {
      let browseHiddenChildCount: number | undefined
      let browseHiddenLabel: string | undefined
      if (
        browseFocus &&
        node.type === 'folder' &&
        browseFocus.parentQualifiesForFocus(node.children)
      ) {
        const breakdown = browseFocus.hiddenChildrenBreakdown(
          node.children,
          node.path,
          activeDocumentPath ?? null,
          openTabPaths
        )
        if (breakdown.total > 0) {
          browseHiddenChildCount = breakdown.total
          browseHiddenLabel = breakdown.label
        }
      }
      onFolderTreeContextMenu?.(node, event, workspaceId, browseHiddenChildCount, browseHiddenLabel)
    },
    [browseFocus, onFolderTreeContextMenu, activeDocumentPath, openTabPaths]
  )

  const { menu: wsRowMenu, menuRef: wsRowMenuRef, openMenu: openWsRowMenu, closeMenu: closeWsRowMenu } =
    useWorkspaceRowContextMenu()

  const activeWorkspaces = useMemo(
    () => workspaces.filter(w => activeWorkspaceIds.includes(w.id)),
    [workspaces, activeWorkspaceIds]
  )
  const { spacesByOrg } = useSpacesForWorkspaces(activeWorkspaceIds)

  const [expandedWorkspacesArr, setExpandedWorkspacesArr] = usePersistedState({
    key: StorageKeys.BROWSE_EXPANDED_WORKSPACES,
    defaultValue: activeWorkspaces.slice(0, 1).map(w => w.id),
    schema: StorageSchemas.stringArray,
  })

  const [workspacesSectionCollapsed, setWorkspacesSectionCollapsed] = usePersistedState({
    key: StorageKeys.BROWSE_WORKSPACES_SECTION_COLLAPSED,
    defaultValue: false,
    schema: StorageSchemas.boolean,
  })
  const expandedWorkspaces = new Set(expandedWorkspacesArr)
  const toggleWs = (id: string) => setExpandedWorkspacesArr(prev => {
    const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return [...s]
  })

  const [expandedExtensionsArr, setExpandedExtensionsArr] = usePersistedState({
    key: StorageKeys.BROWSE_EXPANDED_EXTENSIONS,
    defaultValue: [] as string[],
    schema: StorageSchemas.stringArray,
  })
  const expandedExtensions = new Set(expandedExtensionsArr)
  const toggleExt = (wsId: string, extId: string) => {
    const k = `${wsId}::${extId}`
    setExpandedExtensionsArr(prev => { const s = new Set(prev); s.has(k) ? s.delete(k) : s.add(k); return [...s] })
  }

  const [expandedFolderNodesArr, setExpandedFolderNodesArr] = usePersistedState({
    key: StorageKeys.BROWSE_EXPANDED_FOLDER_NODES,
    defaultValue: [] as string[],
    schema: StorageSchemas.stringArray,
  })

  const persistExpandedPaths = useCallback(
    (paths: string[]) => {
      setExpandedFolderNodesArr(paths)
    },
    [setExpandedFolderNodesArr]
  )

  const [structureRevision, setStructureRevision] = useState(0)

  useEffect(() => {
    const onStructure = (event: Event) => {
      const rev = (event as CustomEvent<{ revision?: number }>).detail?.revision
      if (typeof rev === 'number') setStructureRevision(rev)
    }
    window.addEventListener('flownotes-structure-revision', onStructure as EventListener)
    void fetchWithAuth('/api/content-revision')
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { structureRevision?: number } | null) => {
        if (data && typeof data.structureRevision === 'number') {
          setStructureRevision(data.structureRevision)
        }
      })
      .catch(() => {})
    return () =>
      window.removeEventListener('flownotes-structure-revision', onStructure as EventListener)
  }, [])

  useEffect(() => {
    if (!browseFocus?.registerBrowseTreeSession) return
    const syncSession = () => {
      const contextFolderPaths: string[] = []
      if (selectedProjectId) {
        contextFolderPaths.push(normalizeExplorerPath(selectedProjectId))
      }
      if (activeDocumentPath) {
        const norm = normalizeExplorerPath(activeDocumentPath)
        if (!contextFolderPaths.includes(norm)) contextFolderPaths.push(norm)
      }
      browseFocus.registerBrowseTreeSession({
        expandedFolderPaths: browseExpandedStore.getPathsArray().map((p) => normalizeExplorerPath(p)),
        contextFolderPaths,
      })
    }
    syncSession()
    return browseExpandedStore.subscribeGlobal(syncSession)
  }, [browseFocus?.registerBrowseTreeSession, selectedProjectId, activeDocumentPath])

  const [documentsFocusSegmentsByTree, setDocumentsFocusSegmentsByTree] = usePersistedState({
    key: StorageKeys.BROWSE_DOCUMENTS_FOCUS_SEGMENTS,
    defaultValue: {} as Record<string, string[]>,
    schema: StorageSchemas.documentsFocusByTreeKey,
  })


  // Per-space folder trees: key = `${wsId}::${spaceId}`
  const [folderTrees, setFolderTrees] = useState<Record<string, FolderTreeNode[]>>({})
  const [loadingTrees, setLoadingTrees] = useState<Record<string, boolean>>({})

  const doFetchFolderTree = useCallback(
    async (treeKey: string, treeRoot: string) => {
      const normQueryRoot = normalizeExplorerPath(treeRoot)
      // Cap parallel explorer-tree GETs (boot used to N-way fan-out all spaces).
      return enqueueExplorerTreeFetch(treeKey, async () => {
        setLoadingTrees((prev) => ({ ...prev, [treeKey]: true }))
        try {
          const res = await fetchWithAuth(
            `/api/documents/explorer-tree?root=${encodeURIComponent(normQueryRoot)}`,
            { cache: 'no-store' },
          )
          if (res.ok) {
            const data = await res.json()
            const mapExplorerNode = (f: any): FolderTreeNode => ({
              id: f.type === 'file' && f.id ? String(f.id) : String(f.path ?? f.id),
              name: f.name,
              type: f.type === 'file' ? 'file' : 'folder',
              path: normalizeExplorerPath(String(f.path ?? f.id)),
              children: Array.isArray(f.children) ? f.children.map(mapExplorerNode) : undefined,
              metadata: f.metadata,
            })
            const top = (data.tree ?? []).map(mapExplorerNode)
            let nodes: FolderTreeNode[] = top
            if (
              top.length === 1 &&
              top[0].type === 'folder' &&
              normalizeExplorerPath(top[0].path) === normQueryRoot
            ) {
              nodes = top[0].children ?? []
            }
            setFolderTrees((prev) => ({ ...prev, [treeKey]: nodes }))
            writeExplorerTreeCache(treeKey, structureRevision, nodes)
          }
        } finally {
          setLoadingTrees((prev) => ({ ...prev, [treeKey]: false }))
        }
      })
    },
    [structureRevision],
  )

  const fetchFolderTree = useCallback(
    async (treeKey: string, treeRoot: string) => {
      if (loadingTrees[treeKey]) return
      // Only skip when we already have a loaded tree (including a legitimate empty []).
      // `!folderTrees[treeKey]` is wrong for [] (truthy) and would leave empty stuck forever.
      if (folderTrees[treeKey] === undefined) {
        const cached = readExplorerTreeCache(treeKey, structureRevision)
        if (cached !== null) {
          setFolderTrees((prev) => ({ ...prev, [treeKey]: cached }))
        }
        void doFetchFolderTree(treeKey, treeRoot)
      }
    },
    [folderTrees, loadingTrees, doFetchFolderTree, structureRevision],
  )

  // On mount / when expansion or desk slug resolves: auto-fetch expanded Documents + My documents trees.
  // Persisted my-docs expand used to show "No folders found" because nodes stayed undefined with no fetch.
  useEffect(() => {
    if (!workspaces.length) return
    workspaces.forEach((ws) => {
      if (!ws.contentRoot) return
      const docsKey = `${ws.id}::docs`
      if (expandedExtensions.has(docsKey)) {
        const spaces = getSpacesForWorkspace(ws.id, spacesByOrg[ws.id])
        spaces.forEach((space) => {
          const treeKey = `${ws.id}::${space.id}`
          const treeRoot =
            (ws.contentRoot ?? '').replace(/\/$/, '') +
            (space.path ? '/' + space.path.replace(/\/$/, '') : '')
          if (treeRoot && folderTrees[treeKey] === undefined) {
            void fetchFolderTree(treeKey, treeRoot)
          }
        })
      }
      const myDocsKey = `${ws.id}::my-docs`
      if (!isPersonalWorkspace(ws) && expandedExtensions.has(myDocsKey) && deskSlug) {
        const deskPath = deskPathForMember(ws, deskSlug)
        const deskPathNorm = deskPath ? normalizeExplorerPath(deskPath) : ''
        const myDocsTreeKey = `${ws.id}::desk`
        if (deskPathNorm && folderTrees[myDocsTreeKey] === undefined) {
          void fetchFolderTree(myDocsTreeKey, deskPathNorm)
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaces, spacesByOrg, expandedExtensionsArr, deskSlug])

  // Desk slug can resolve after first expand; force desk tree refetch when path becomes available.
  const prevDeskSlugRef = useRef<string | null>(null)
  useEffect(() => {
    const prev = prevDeskSlugRef.current
    prevDeskSlugRef.current = deskSlug
    if (!deskSlug || prev === deskSlug) return
    const expanded = new Set(expandedExtensionsArr)
    activeWorkspaces.forEach((ws) => {
      if (isPersonalWorkspace(ws)) return
      const deskPath = deskPathForMember(ws, deskSlug)
      const deskPathNorm = deskPath ? normalizeExplorerPath(deskPath) : ''
      if (!deskPathNorm) return
      const myDocsTreeKey = `${ws.id}::desk`
      clearExplorerTreeCache(myDocsTreeKey)
      setFolderTrees((prevTrees) => {
        if (prevTrees[myDocsTreeKey] === undefined) return prevTrees
        const next = { ...prevTrees }
        delete next[myDocsTreeKey]
        return next
      })
      if (expanded.has(`${ws.id}::my-docs`)) {
        void doFetchFolderTree(myDocsTreeKey, deskPathNorm)
      }
    })
  }, [deskSlug, activeWorkspaces, expandedExtensionsArr, doFetchFolderTree])

  // Refetch explorer trees when ResizableLayout bumps explorerRefreshToken (create/delete/rename — not content-only revision).
  // Listener lives in LeftSidebar so we do not miss events while BrowsePanel is unmounted (Activity / Trash / Search / etc.).
  useEffect(() => {
    if (explorerRefreshToken === 0) return
    activeWorkspaces.forEach((ws) => {
      if (!ws.contentRoot) return
      const spaces = getSpacesForWorkspace(ws.id, spacesByOrg[ws.id])
      spaces.forEach((space) => {
        const treeKey = `${ws.id}::${space.id}`
        const treeRoot =
          (ws.contentRoot ?? '').replace(/\/$/, '') +
          (space.path ? '/' + space.path.replace(/\/$/, '') : '')
        if (treeRoot) void doFetchFolderTree(treeKey, treeRoot)
      })
      if (!isPersonalWorkspace(ws) && deskSlug) {
        const deskPath = deskPathForMember(ws, deskSlug)
        const deskPathNorm = deskPath ? normalizeExplorerPath(deskPath) : ''
        if (deskPathNorm) {
          void doFetchFolderTree(`${ws.id}::desk`, deskPathNorm)
        }
      }
    })
  }, [explorerRefreshToken, activeWorkspaces, spacesByOrg, doFetchFolderTree, deskSlug])

  useEffect(() => {
    if (!browseFocus?.focusBrowse) return
    const entries: { folderPath: string; timestampMs: number }[] = []
    for (const nodes of Object.values(folderTrees)) {
      if (nodes?.length) {
        entries.push(...collectFolderMaxUpdatedFromExplorerNodes(nodes))
      }
    }
    if (entries.length) browseFocus.mergeTreeFolderActivity(entries)
  }, [folderTrees, browseFocus?.focusBrowse, browseFocus?.mergeTreeFolderActivity])

  // Listen for show-document-in-browse — expand tree for new locations; preserve drill-down when browsing a folder.
  useEffect(() => {
    const handleReveal = (
      event: CustomEvent<{
        filePath: string
        documentId: string
        forceRefresh?: boolean
        scrollToNode?: boolean
      }>
    ) => {
      const { filePath, forceRefresh = false, scrollToNode = false } = event.detail
      if (!filePath) return

      const savedScrollTop = browseScrollRef.current?.scrollTop ?? 0

      setWorkspacesSectionCollapsed(false)

      // Find which workspace this path belongs to
      const ws = workspaces.find(w => {
        const prefix = (w.contentRoot ?? '').replace(/\/$/, '')
        return prefix !== '' && (filePath === prefix || filePath.startsWith(prefix + '/'))
      })
      if (!ws) return

      const spaces = getSpacesForWorkspace(ws.id, spacesByOrg[ws.id])
      const prefix = (ws.contentRoot ?? '').replace(/\/$/, '')
      const space = spaces.find(s => {
        const spacePrefix = prefix + (s.path ? '/' + s.path.replace(/\/$/, '') : '')
        return spacePrefix === '' || filePath === spacePrefix || filePath.startsWith(spacePrefix + '/')
      }) ?? spaces[0]
      const treeKey = ws.contentRoot && space ? `${ws.id}::${space.id}` : null
      const treeRoot =
        ws.contentRoot && space
          ? prefix + (space.path ? '/' + space.path.replace(/\/$/, '') : '')
          : null

      const preserveBrowseState = shouldPreserveBrowseStateOnDocumentOpen(filePath, {
        scrollToNode,
        selectedProjectId,
        focusSegments: treeKey ? documentsFocusSegmentsByTree[treeKey] ?? [] : [],
        treeRoot: treeRoot ?? undefined,
      })

      // Reset drill-down only when revealing a doc outside the current browse context.
      if (!preserveBrowseState) {
        setDocumentsFocusSegmentsByTree(prev => {
          const next = { ...prev }
          for (const k of Object.keys(next)) {
            if (k.startsWith(`${ws.id}::`)) delete next[k]
          }
          return next
        })
      }

      // Expand the workspace
      setExpandedWorkspacesArr(prev => {
        const s = new Set(prev)
        s.add(ws.id)
        return [...s]
      })

      // Expand the Documents section for this workspace
      const docsKey = `${ws.id}::docs`
      const fileNorm = normalizeExplorerPath(filePath)
      const pinnedExpandKey = expandKeyForPinnedWorkspace(ws.id)
      const shouldExpandPinned = shortcuts.some(
        (shortcutItem) =>
          shortcutBelongsToWorkspace(shortcutItem, ws) &&
          normalizeExplorerPath(String(shortcutItem.document_path ?? '')) === fileNorm,
      )
      setExpandedExtensionsArr(prev => {
        const s = new Set(prev)
        s.add(docsKey)
        if (shouldExpandPinned) s.add(pinnedExpandKey)
        return [...s]
      })

      // Fetch or refresh the folder tree for the space containing this path.
      // Only force re-fetch when opening a NEW doc (ingestion, createNewTab) so new folders appear.
      // Tab switch: use cached tree if available to avoid "Loading…" delay.
      if (treeKey && treeRoot) {
        if (forceRefresh) {
          clearExplorerTreeCache(treeKey)
          setFolderTrees(prev => {
            const next = { ...prev }
            delete next[treeKey]
            return next
          })
          doFetchFolderTree(treeKey, treeRoot)
        } else {
          fetchFolderTree(treeKey, treeRoot)
        }
      }

      // Expand ancestor folders when the doc is outside the current drill-down / hub folder.
      if (!preserveBrowseState) {
        const parts = filePath.split('/')
        const ancestorPaths: string[] = []
        for (let i = 1; i < parts.length; i++) {
          ancestorPaths.push(parts.slice(0, i).join('/'))
        }
        browseExpandedStore.addPaths(ancestorPaths)
      }

      if (scrollToNode) {
        setTimeout(() => {
          const testId = testIds.explorerNode.row(filePath)
          const el = document.querySelector(`[data-testid="${testId}"]`)
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          }
        }, 350)
      } else if (preserveBrowseState) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (browseScrollRef.current) {
              browseScrollRef.current.scrollTop = savedScrollTop
            }
          })
        })
      }
    }

    window.addEventListener('show-document-in-browse', handleReveal as EventListener)
    return () => window.removeEventListener('show-document-in-browse', handleReveal as EventListener)
  }, [
    workspaces,
    shortcuts,
    spacesByOrg,
    doFetchFolderTree,
    fetchFolderTree,
    setExpandedWorkspacesArr,
    setExpandedExtensionsArr,
    setWorkspacesSectionCollapsed,
    setDocumentsFocusSegmentsByTree,
    documentsFocusSegmentsByTree,
    selectedProjectId,
  ])

  // Sync browse tree with hub folder navigation: expand ancestors, keep tree visible (SWR refresh).
  useEffect(() => {
    const handleShowFolderInBrowse = (event: CustomEvent<{ folderPath: string }>) => {
      const folderPath = event.detail?.folderPath
      if (!folderPath) return
      const normFolderPath = normalizeExplorerPath(folderPath)

      setWorkspacesSectionCollapsed(false)

      const ws = workspaces.find(w => {
        const prefix = (w.contentRoot ?? '').replace(/\/$/, '')
        return prefix !== '' && (folderPath === prefix || folderPath.startsWith(prefix + '/'))
      })
      if (!ws) return

      setExpandedWorkspacesArr(prev => {
        const s = new Set(prev)
        s.add(ws.id)
        return [...s]
      })

      const docsKey = `${ws.id}::docs`
      setExpandedExtensionsArr(prev => {
        const s = new Set(prev)
        s.add(docsKey)
        return [...s]
      })

      const spaces = getSpacesForWorkspace(ws.id, spacesByOrg[ws.id])
      const prefix = (ws.contentRoot ?? '').replace(/\/$/, '')
      const space = spaces.find(s => {
        const spacePrefix = prefix + (s.path ? '/' + s.path.replace(/\/$/, '') : '')
        return spacePrefix === '' || folderPath === spacePrefix || folderPath.startsWith(spacePrefix + '/')
      }) ?? spaces[0]
      if (ws.contentRoot && space) {
        const treeKey = `${ws.id}::${space.id}`
        const treeRoot = prefix + (space.path ? '/' + space.path.replace(/\/$/, '') : '')
        if (treeRoot) {
          const existingNodes = folderTrees[treeKey]
          if (existingNodes === undefined) {
            void fetchFolderTree(treeKey, treeRoot)
          } else if (!treeContainsFolderPath(existingNodes, normFolderPath)) {
            void doFetchFolderTree(treeKey, treeRoot)
          }
        }
      }

      const parts = folderPath.split('/').filter(Boolean)
      const pathsToExpand: string[] = []
      for (let i = 1; i <= parts.length; i++) {
        pathsToExpand.push(parts.slice(0, i).join('/'))
      }
      browseExpandedStore.addPaths(pathsToExpand)

      // Keep the clicked folder row in view after expand state commits (same pattern as reveal-in-sidebar).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const testId = testIds.explorerNode.row(folderPath)
          const el = document.querySelector(`[data-testid="${testId}"]`)
          el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        })
      })
    }

    window.addEventListener('show-folder-in-browse', handleShowFolderInBrowse as EventListener)
    return () => window.removeEventListener('show-folder-in-browse', handleShowFolderInBrowse as EventListener)
  }, [
    workspaces,
    spacesByOrg,
    folderTrees,
    fetchFolderTree,
    doFetchFolderTree,
    setExpandedWorkspacesArr,
    setExpandedExtensionsArr,
    setWorkspacesSectionCollapsed,
  ])

  // No auto-scroll on activeDocumentPath changes — use "Reveal in sidebar" (tab menu or … menu).

  return (
    <>
    <BrowseExpandedProvider
      initialPaths={expandedFolderNodesArr}
      onPathsChange={persistExpandedPaths}
    >
    <div className="flex flex-col flex-1 min-h-0">
      {/* Browse tree (Home, Workspaces) */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {/* Single scroll viewport for Browse (vertical + horizontal when needed). No nested overflow-x on Documents tree. */}
        <div ref={browseScrollRef} className="min-h-0 min-w-0 flex-1 overflow-auto overscroll-contain">
        <div
          className="min-w-0 max-w-full"
          style={{ '--ws-color': 'hsl(var(--primary))' } as React.CSSProperties}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={onShortcutDragStart}
            onDragEnd={onShortcutDragEnd}
          >

          {/* Home */}
          <div className="pt-2 px-2">
            <div role="button" onClick={onHomeClick}
              className={`ws-nav-item flex items-center gap-2 px-3 py-1.5 text-sm select-none rounded-sm ${activeTabId === 'home' ? 'ws-nav-item--active text-sidebar-foreground' : 'text-sidebar-foreground/70'}`}>
              <Home className="w-4 h-4 flex-none" /><span>Home</span>
            </div>
          </div>

          {/* Workspaces — collapsible panel, same pattern as Open Tabs */}
          <div className="px-2 mt-2">
            <button
              type="button"
              aria-expanded={!workspacesSectionCollapsed}
              onClick={() => setWorkspacesSectionCollapsed(c => !c)}
              className="flex w-full min-w-0 items-center gap-0.5 rounded-sm px-1 py-1 text-left text-sidebar-foreground/90 hover:bg-sidebar-accent/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sidebar-ring"
              data-testid={testIds.leftSidebar.workspacesBrowseHeading}
            >
              <span
                className="flex h-5 w-5 flex-none items-center justify-center text-muted-foreground transition-transform duration-150"
                style={{ transform: workspacesSectionCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
                aria-hidden
              >
                <ChevronDown className="h-3 w-3" />
              </span>
              <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">
                Workspaces
              </span>
              <span className="ml-0.5 text-[10px] font-medium tabular-nums text-muted-foreground/55">
                ({activeWorkspaces.length})
              </span>
            </button>
          </div>
          {!workspacesSectionCollapsed && (
          <div className="px-2 mt-1">
            {activeWorkspaces.length === 0 && (
              <div className="px-3 py-2 text-xs text-muted-foreground">No active workspaces</div>
            )}
            {activeWorkspaces.map(ws => {
              const wsExpanded = expandedWorkspaces.has(ws.id)
              const wsColor = ws.color ?? 'hsl(var(--primary))'
              const docsKey = `${ws.id}::docs`
              const docsExpanded = expandedExtensions.has(docsKey)
              const spaces = getSpacesForWorkspace(ws.id, spacesByOrg[ws.id])
              /** Same root as the first space in the Documents tree — explorer list view for this workspace. */
              const documentsRootPath =
                ws.contentRoot?.trim()
                  ? normalizeExplorerPath(
                      (ws.contentRoot ?? '').replace(/\/$/, '') +
                        (spaces[0]?.path ? '/' + spaces[0].path.replace(/\/$/, '') : ''),
                    )
                  : ''

              const showMyDocuments = !isPersonalWorkspace(ws)
              const deskPath = showMyDocuments ? deskPathForMember(ws, deskSlug) : ''
              const myDocsKey = `${ws.id}::my-docs`
              const myDocsExpanded = expandedExtensions.has(myDocsKey)
              const myDocsTreeKey = `${ws.id}::desk`
              const deskPathNorm = deskPath ? normalizeExplorerPath(deskPath) : ''
              const deskBelongs =
                Boolean(deskPathNorm) &&
                activeDocumentPath != null &&
                (normalizeExplorerPath(activeDocumentPath) === deskPathNorm ||
                  normalizeExplorerPath(activeDocumentPath).startsWith(deskPathNorm + '/'))
              const isDeskHomeTab = (activeTabId ?? '').startsWith(`desk-home-${ws.id}`)
              const isWsHomeTab = (activeTabId ?? '').startsWith(`workspace-home-${ws.id}`)
              const deskPathExact =
                Boolean(deskPathNorm) &&
                activeDocumentPath != null &&
                normalizeExplorerPath(activeDocumentPath) === deskPathNorm
              // Leaf active only for landing tabs / exact desk path - not nested docs.
              const isMyDocsActive = isDeskHomeTab || deskPathExact
              const isMyDocsContains = deskBelongs && !isMyDocsActive

              const wsPrefix = (ws.contentRoot ?? '').replace(/\/$/, '')
              const docBelongsToWs = activeDocumentPath != null && wsPrefix !== '' &&
                (activeDocumentPath === wsPrefix || activeDocumentPath.startsWith(wsPrefix + '/'))
              // Leaf: workspace / desk home landing only. Nested docs = contains.
              const isWsActive = isWsHomeTab || isDeskHomeTab
              const isWsContains = docBelongsToWs && !isWsActive
              const documentsRootNorm = documentsRootPath
                ? normalizeExplorerPath(documentsRootPath)
                : ''
              const docsPathExact =
                Boolean(documentsRootNorm) &&
                activeDocumentPath != null &&
                normalizeExplorerPath(activeDocumentPath) === documentsRootNorm
              const isDocsActive = docsPathExact
              const isDocsContains = docBelongsToWs && !deskBelongs && !isDocsActive

              return (
                <div key={ws.id} className={`ws-nav-section mb-0.5 min-w-0 ${wsExpanded ? 'pb-2 mb-2' : ''}`}
                  style={{ '--ws-color': wsColor } as React.CSSProperties}>

                  {/* Workspace header */}
                  <div
                    className={`ws-nav-section-header group flex items-center gap-2 px-2 py-1 select-none ${
                      isWsActive
                        ? 'ws-nav-section-header--active text-sidebar-foreground'
                        : isWsContains
                          ? 'ws-nav-section-header--contains'
                          : 'text-sidebar-foreground/70'
                    }`}
                    onContextMenu={(e) => openWsRowMenu(ws.id, e)}
                  >
                    <span className="flex-none w-3 h-3 flex items-center justify-center text-muted-foreground rounded hover:bg-sidebar-accent/60 cursor-pointer transition-transform duration-200 ease-out"
                      style={{ transform: wsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}
                      onClick={() => toggleWs(ws.id)}>
                      <ChevronDown className="w-3 h-3" />
                    </span>
                    <span className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                      onClick={() => window.dispatchEvent(new CustomEvent('open-workspace-home', { detail: { workspaceId: ws.id } }))}>
                      <span
                        className="flex-none w-7 h-7 rounded flex items-center justify-center"
                        style={{ backgroundColor: workspaceAccentFill(wsColor) }}
                      >
                        <WorkspaceIcon icon={ws.icon} color={WS_ICON_ON_ACCENT} name={ws.name} size={14} />
                      </span>
                      <span className="flex-1 truncate font-semibold text-[15px] leading-snug">{ws.name}</span>
                    </span>
                  </div>

                  {/* Workspace children — animated expand */}
                  <NavExpandPanel open={wsExpanded} inset="workspace">
                      {/* My documents — Workspace Desk (ADR-0019). Hidden on personal
                          workspaces: desk path is empty/redundant with Documents. */}
                      {showMyDocuments ? (
                      <div className="min-w-0">
                        <WsNavBrowseRow
                          role="button"
                          data-testid={testIds.leftSidebar.workspaceMyDocumentsRow(ws.id)}
                          padLeft={WS_NAV_CHILD_PAD}
                          active={isMyDocsActive}
                          contains={isMyDocsContains}
                          className={!deskPath ? 'opacity-50' : undefined}
                          chevron={
                            deskPath
                              ? {
                                  expanded: myDocsExpanded,
                                  onClick: () => {
                                    toggleExt(ws.id, 'my-docs')
                                    if (!expandedExtensions.has(myDocsKey) && deskPathNorm) {
                                      void fetchFolderTree(myDocsTreeKey, deskPathNorm)
                                    }
                                  },
                                }
                              : undefined
                          }
                          icon={<User className="w-3.5 h-3.5 flex-none" />}
                          label="My documents"
                          onClick={() => {
                            if (!deskPath || !deskSlug) return
                            window.dispatchEvent(
                              new CustomEvent('open-desk-home', {
                                detail: {
                                  workspaceId: ws.id,
                                  deskPath,
                                  deskSlug,
                                },
                              }),
                            )
                          }}
                          onDoubleClick={(e) => e.preventDefault()}
                        />
                        <NavExpandPanel open={myDocsExpanded && Boolean(deskPath)}>
                          <div className="ws-nav-inline-group min-h-0 min-w-0 max-w-full">
                            {(() => {
                              const isLoading = !!loadingTrees[myDocsTreeKey]
                              const nodes = folderTrees[myDocsTreeKey]
                              const isInitialTreeLoad = nodes === undefined && isLoading
                              const isTreeRefreshing = nodes !== undefined && isLoading
                              if (!deskPathNorm) {
                                return (
                                  <p className="px-3 py-1 text-[11px] text-muted-foreground">
                                    Desk path unavailable
                                  </p>
                                )
                              }
                              return (
                                <DocumentsExplorerInlineSection
                                  treeKey={myDocsTreeKey}
                                  treeRoot={deskPathNorm}
                                  wsColor={wsColor}
                                  workspaceId={ws.id}
                                  nodes={nodes}
                                  loading={isInitialTreeLoad || nodes === undefined}
                                  refreshing={isTreeRefreshing}
                                  emptyLabel="Your desk is empty"
                                  focusSegments={documentsFocusSegmentsByTree[myDocsTreeKey] ?? []}
                                  onFocusSegments={(segs) =>
                                    setDocumentsFocusSegmentsByTree((prev) => ({
                                      ...prev,
                                      [myDocsTreeKey]: segs,
                                    }))
                                  }
                                  activeDocumentPath={activeDocumentPath ?? null}
                                  onFolderClick={(path, e) => onFolderClick?.(path, e)}
                                  onFileClick={(path, e) => onDocumentClick?.(path, e)}
                                  onFileDoubleClick={(path) => {
                                    onDocumentClick?.(path)
                                    window.dispatchEvent(
                                      new CustomEvent('pin-document-by-path', { detail: { path } }),
                                    )
                                  }}
                                  onContextMenu={
                                    onFolderTreeContextMenu
                                      ? (node, e) => handleTreeContextMenu(node, e, ws.id)
                                      : undefined
                                  }
                                  openTabPaths={openTabPaths}
                                  openTabIndex={openTabIndex}
                                />
                              )
                            })()}
                          </div>
                        </NavExpandPanel>
                      </div>
                      ) : null}

                      {!shortcutsLoading && (() => {
                        const wsPins = pinnedShortcutsForWorkspace(shortcuts, ws)
                        if (wsPins.length === 0) return null
                        const pinKey = expandKeyForPinnedWorkspace(ws.id)
                        const isPinnedExpanded = expandedExtensions.has(pinKey)
                        const isAnyPinActive = wsPins.some((shortcut) => {
                          const pathMatch =
                            Boolean(activeDocumentPath) &&
                            (normalizeExplorerPath(activeDocumentPath!) === normalizeExplorerPath(String(shortcut.document_path ?? '')) ||
                             normalizeExplorerPath(activeDocumentPath!) === normalizeExplorerPath(String(shortcut.folder_path ?? '')))
                          return (
                            activeTabId === shortcut.document_id ||
                            activeTabId === `doc-${shortcut.document_id}` ||
                            pathMatch
                          )
                        })
                        return (
                          <div className="min-w-0">
                            <WsNavBrowseRow
                              role="button"
                              tabIndex={0}
                              aria-expanded={isPinnedExpanded}
                              data-testid={testIds.leftSidebar.workspacePinnedParentRow(ws.id)}
                              padLeft={WS_NAV_CHILD_PAD}
                              active={false}
                              contains={isAnyPinActive}
                              chevron={{
                                expanded: isPinnedExpanded,
                                onClick: () => toggleExt(ws.id, SIDEBAR_PINS_EXPAND_ID),
                              }}
                              icon={<Pin className="w-3.5 h-3.5 flex-none" aria-hidden />}
                              label="Pinned"
                              onClick={() => toggleExt(ws.id, SIDEBAR_PINS_EXPAND_ID)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  toggleExt(ws.id, SIDEBAR_PINS_EXPAND_ID)
                                }
                              }}
                            />
                            <NavExpandPanel open={isPinnedExpanded}>
                                <SortableContext items={wsPins.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                                  <div className="ws-nav-inline-group min-w-0 max-w-full">
                                    {wsPins.map((shortcut) => {
                                      const pathMatch =
                                        Boolean(activeDocumentPath) &&
                                        (normalizeExplorerPath(activeDocumentPath!) === normalizeExplorerPath(String(shortcut.document_path ?? '')) ||
                                         normalizeExplorerPath(activeDocumentPath!) === normalizeExplorerPath(String(shortcut.folder_path ?? '')))
                                      const isActive =
                                        activeTabId === shortcut.document_id ||
                                        activeTabId === `doc-${shortcut.document_id}` ||
                                        !!pathMatch
                                      const isOpen =
                                        !isActive &&
                                        isOpenInTabs(openTabIndex, {
                                          path: shortcut.document_path ?? shortcut.folder_path,
                                          documentId: shortcut.document_id,
                                        })
                                      return (
                                        <SortableShortcutItem
                                          key={shortcut.id}
                                          shortcut={shortcut}
                                          isActive={isActive}
                                          isOpen={isOpen}
                                          nested
                                          workspaceId={ws.id}
                                          onDocumentClick={onDocumentClick}
                                          onFolderClick={onFolderClick}
                                        />
                                      )
                                    })}
                                  </div>
                                </SortableContext>
                            </NavExpandPanel>
                          </div>
                        )
                      })()}

                      {/* Documents — inline folder tree expansion (per-space) */}
                      <div className="min-w-0">
                        <WsNavBrowseRow
                          role="button"
                          data-testid={testIds.leftSidebar.workspaceDocumentsRow(ws.id)}
                          padLeft={WS_NAV_CHILD_PAD}
                          active={isDocsActive}
                          contains={isDocsContains}
                          chevron={{
                            expanded: docsExpanded,
                            onClick: () => {
                              if (ws.contentRoot) {
                                toggleExt(ws.id, 'docs')
                                if (!expandedExtensions.has(docsKey)) {
                                  spaces.forEach(space => {
                                    const treeKey = `${ws.id}::${space.id}`
                                    const treeRoot = (ws.contentRoot ?? '').replace(/\/$/, '') + (space.path ? '/' + space.path.replace(/\/$/, '') : '')
                                    if (treeRoot) fetchFolderTree(treeKey, treeRoot)
                                  })
                                }
                              }
                            },
                          }}
                          icon={<FileText className="w-3.5 h-3.5 flex-none" />}
                          label="Documents"
                          onClick={() => {
                            setDocumentsFocusSegmentsByTree(prev => {
                              const next = { ...prev }
                              for (const k of Object.keys(next)) {
                                if (k.startsWith(`${ws.id}::`)) delete next[k]
                              }
                              return next
                            })
                            onSetTab('documents-tree' as SidebarTab)
                            if (documentsRootPath) {
                              onFolderClick?.(documentsRootPath)
                            }
                          }}
                          onDoubleClick={(e) => e.preventDefault()}
                          onContextMenu={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            const hasRoot = Boolean(documentsRootPath)
                            window.dispatchEvent(new CustomEvent('open-context-menu', {
                              detail: {
                                x: e.clientX,
                                y: e.clientY,
                                context: {
                                  type: 'folder',
                                  value: 'Documents',
                                  path: hasRoot ? documentsRootPath : '',
                                  workspaceId: ws.id,
                                  ...(hasRoot ? {} : { missingContentRoot: true }),
                                },
                              },
                            }))
                          }}
                        />
                        {/* Documents tree — animated expand; outer Browse column scrolls (no nested max-h scroll) */}
                        <NavExpandPanel open={docsExpanded}>
                            <div className="ws-nav-inline-group min-h-0 min-w-0 max-w-full">
                              {(() => {
                                const showSpaceLabels = spaces.length > 1
                                return spaces.map(space => {
                                  const treeKey = `${ws.id}::${space.id}`
                                  const isLoading = !!loadingTrees[treeKey]
                                  const nodes = folderTrees[treeKey]
                                  const isInitialTreeLoad = nodes === undefined && isLoading
                                  const isTreeRefreshing = nodes !== undefined && isLoading
                                  const treeRootNorm = normalizeExplorerPath(
                                    (ws.contentRoot ?? '').replace(/\/$/, '') +
                                      (space.path ? '/' + space.path.replace(/\/$/, '') : ''),
                                  )
                                  return (
                                    <div key={space.id} className="min-w-0">
                                      {showSpaceLabels && (
                                        <div className="px-3 py-0.5 text-[11px] font-medium text-muted-foreground/80 truncate">{space.name}</div>
                                      )}
                                      <DocumentsExplorerInlineSection
                                        treeKey={treeKey}
                                        treeRoot={treeRootNorm}
                                        wsColor={wsColor}
                                        workspaceId={ws.id}
                                        nodes={nodes}
                                        loading={isInitialTreeLoad}
                                        refreshing={isTreeRefreshing}
                                        focusSegments={documentsFocusSegmentsByTree[treeKey] ?? []}
                                        onFocusSegments={segs =>
                                          setDocumentsFocusSegmentsByTree(prev => ({ ...prev, [treeKey]: segs }))
                                        }
                                        activeDocumentPath={activeDocumentPath ?? null}
                                        onFolderClick={(path, e) => onFolderClick?.(path, e)}
                                        onFileClick={(path, e) => onDocumentClick?.(path, e)}
                                        onFileDoubleClick={path => {
                                          onDocumentClick?.(path)
                                          window.dispatchEvent(new CustomEvent('pin-document-by-path', { detail: { path } }))
                                        }}
                                        onContextMenu={
                                          onFolderTreeContextMenu
                                            ? (node, e) => handleTreeContextMenu(node, e, ws.id)
                                            : undefined
                                        }
                                        openTabPaths={openTabPaths}
                                        openTabIndex={openTabIndex}
                                      />
                                    </div>
                                  )
                                })
                              })()}
                            </div>
                        </NavExpandPanel>
                      </div>

                      <WsNavBrowseRow
                        role="button"
                        data-testid={testIds.leftSidebar.workspacePeopleRow(ws.id)}
                        padLeft={WS_NAV_CHILD_PAD}
                        active={
                          openTabs.some(
                            (t) =>
                              t.id === activeTabId &&
                              t.type === 'person-view' &&
                              t.workspaceId === ws.id,
                          )
                        }
                        icon={<User className="w-3.5 h-3.5 flex-none" />}
                        label="People"
                        onClick={() => dispatchOpenPeopleIndex(ws.id)}
                      />

                      {/* Remaining Browse-nested extensions (not Basecamp — rail graduate). Knowledge Hub stays here this slice. */}
                      <WorkspaceExtensionItems workspaceId={ws.id} integrations={configuredIntegrations}>
                        {(wsExtensions) =>
                          (wsExtensions ?? [])
                            .filter((integration) => integration.id !== 'basecamp')
                            .map((integration) => {
                              const extKey = `${ws.id}::${integration.id}`
                              const isExtExpanded = expandedExtensions.has(extKey)
                              const activeTab = openTabs.find((t) => t.id === activeTabId)
                              const isExtActive =
                                integration.id === 'knowledge-hub'
                                  ? (() => {
                                      if (activeTab?.type !== 'integration-knowledge-hub') return false
                                      const tabWs =
                                        activeTab?.integrationData?.organizationId ??
                                        activeTab?.workspaceId
                                      return tabWs ? tabWs === ws.id : false
                                    })()
                                  : activeTab?.type === `integration-${integration.id}`
                              return (
                                <div key={integration.id} className="min-w-0">
                                  {integration.id === 'knowledge-hub' ? (
                                    <KnowledgeHubBrowseSection
                                      workspaceId={ws.id}
                                      padLeft={WS_NAV_CHILD_PAD}
                                      active={isExtActive}
                                      expanded={isExtExpanded}
                                      onToggleExpand={() => toggleExt(ws.id, integration.id)}
                                      icon={<integration.icon className="w-3.5 h-3.5 flex-none" />}
                                      label={integration.name}
                                      onSetTab={(tab) => onSetTab(tab as SidebarTab)}
                                      activeTabId={activeTabId}
                                      activeFilter={
                                        activeTab?.integrationData?.statusFilter === 'needs-review'
                                          ? 'needs-review'
                                          : activeTab?.integrationData?.statusFilter ===
                                              'review-requested'
                                            ? 'review-requested'
                                            : null
                                      }
                                    />
                                  ) : (
                                    <WsNavBrowseRow
                                      role="button"
                                      padLeft={WS_NAV_CHILD_PAD}
                                      active={isExtActive}
                                      chevron={{
                                        expanded: isExtExpanded,
                                        onClick: () => toggleExt(ws.id, integration.id),
                                      }}
                                      icon={<integration.icon className="w-3.5 h-3.5 flex-none" />}
                                      label={integration.name}
                                      onClick={() => {
                                        if (integration.id === 'voice-memos') {
                                          onInboxClick?.()
                                          window.dispatchEvent(
                                            new CustomEvent('inbox-filter-change', {
                                              detail: { kindFilter: 'voice' },
                                            }),
                                          )
                                        } else {
                                          onSetTab(`integration-${integration.id}` as SidebarTab)
                                        }
                                      }}
                                    />
                                  )}
                                </div>
                              )
                            })
                        }
                      </WorkspaceExtensionItems>

                      <hr className="ws-nav-section-separator" />
                  </NavExpandPanel>
                </div>
              )
            })}
          </div>
          )}
<DragOverlay>
            {activeShortcutId
              ? (() => {
                  const draggedShortcut = shortcuts.find((s) => s.id === activeShortcutId)
                  if (!draggedShortcut) return null
                  const displayIcon = draggedShortcut.icon
                    ? draggedShortcut.icon
                    : getShortcutIcon(draggedShortcut)
                  const displayLabel = draggedShortcut.label || draggedShortcut.document_title || draggedShortcut.folder_path?.split('/').filter(Boolean).pop() || 'Untitled'
                  return (
                    <div className="ws-nav-item flex items-center gap-2 px-3 py-1.5 text-sm rounded-sm bg-sidebar text-sidebar-foreground shadow-lg">
                      {displayIcon}
                      <span className="truncate">{displayLabel}</span>
                    </div>
                  )
                })()
              : null}
          </DragOverlay>
          </DndContext>
        </div>
        </div>
      </div>
    </div>
    </BrowseExpandedProvider>
    {onToggleWorkspace && (
      <WorkspaceRowContextMenuPortal
        menu={wsRowMenu}
        menuRef={wsRowMenuRef}
        onClose={closeWsRowMenu}
        onToggleWorkspace={onToggleWorkspace}
        onHideOtherWorkspaces={onHideOtherWorkspaces}
        activeWorkspaceIds={activeWorkspaceIds}
      />
    )}
    </>
  )
}
